import { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Alert } from '@mui/material';
import { Box, Stack } from '@mui/system';

import ModalNotification from '@/shared/components/dialogs/ModalNotification';
import WaveUIForm_V2 from '@/shared/components/forms/WaveUIForm_V2';
import FieldType from '@/shared/components/forms/WaveUIForm_V2/types/fieldType.enum';
import BookingsService from '@/shared/types/bookings/service.type';

type BookingServiceRemoveContactsDialogProps = {
  bookingService: BookingsService;
  loading: boolean;
  errors?: string[];
  onClose: () => void;
  proceed: (selectedContactsIds: string[]) => void;
};

const BookingServiceRemoveContactsDialog: FC<
  BookingServiceRemoveContactsDialogProps
> = ({ bookingService, loading, errors, onClose, proceed }) => {
  const { t } = useTranslation(['bookingsServices', 'forms', 'common']);

  const [selectedContacts, setSelectedContacts] = useState<string[]>([]);
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>[]
  >([]);

  const onValidate = () => {
    const errors: Record<string, string>[] = [];

    if (!selectedContacts || selectedContacts.length === 0) {
      errors.push({
        booking_service_contact_id: t(
          'forms:booking_service_contact_id_required',
        ),
      });
    }

    setValidationErrors(errors);
    return errors.length === 0;
  };

  if (errors && errors.length > 0) {
    return (
      <ModalNotification
        size='xs'
        fullWidth
        open
        onClose={onClose}
        dialogProps={{
          title: t('bookingsServices:removeContacts'),
          content: (
            <Stack spacing={2} useFlexGap>
              {t('bookingsServices:removeContactsError')}
              {errors.map((error) => (
                <Alert key={error} severity='error'>
                  {error}
                </Alert>
              ))}
            </Stack>
          ),
          actions: [
            {
              label: t('common:close'),
              onClick: onClose,
            },
          ],
        }}
      />
    );
  }

  return (
    <ModalNotification
      size='xs'
      fullWidth
      open
      onClose={onClose}
      dialogProps={{
        title: t('bookingsServices:removeContacts'),
        content: (
          <Box sx={{ py: 1 }}>
            <WaveUIForm_V2
              fields={[
                {
                  field_type: FieldType.MultiSelect,
                  field_props: {
                    name: 'booking_service_contact_id',
                    label: t('forms:booking_service_contact_id'),
                    value: selectedContacts,
                    options: bookingService.contacts.map((contact) => ({
                      value: contact.booking_service_contact_id,
                      label: `${contact.booking_contact_name} - ${contact.calendar_name}`,
                    })),
                    multiple: true,
                    placeholder: t(
                      'forms:booking_service_contact_id_placeholder',
                    ),
                    error: validationErrors.length > 0,
                    helperText:
                      validationErrors.length > 0
                        ? validationErrors[0].booking_service_contact_id
                        : undefined,
                  },
                },
              ]}
              getFieldsValues={(value) => {
                const newValue = value.booking_service_contact_id as string[];
                setSelectedContacts(newValue);
              }}
            />
          </Box>
        ),
        actions: [
          {
            label: t('common:close'),
            onClick: onClose,
          },
          {
            label: t('common:proceed'),
            variant: 'contained',
            color: 'error',
            onClick: () => {
              if (!loading && onValidate()) {
                proceed(selectedContacts);
              }
            },
          },
        ],
      }}
    />
  );
};

export default BookingServiceRemoveContactsDialog;
